import React from 'react';
import { Box, Button, Flex, Icon, Switch, Text, Tooltip, useSnackbar } from 'pounceV2';
import { useTheme } from '@mui/material';
import { ApolloError } from '@apollo/client';
import { AlertStatus, CommentFormat, Permission } from 'Generated/schema';
import useAuth from 'Hooks/useAuth';
import RoleRestrictedAccess from 'Components/utils/RoleRestrictedAccess';
import { useCreateComment } from 'Pages/Alerts/AlertDetails/graphql/createComment.generated';
import { useUpdateAlertStatusById } from 'Source/graphql/queries';
import { useUpdateAlertsAssigneeById } from 'Source/graphql/queries/updateAlertsAssigneeById.generated';
import {
  AlertActivityHistoryDocument,
  ALERT_ACTIVITY_PAGE_SIZE,
} from 'Pages/Alerts/AlertDetails/common/AlertHistorySection';
import useEventHandler from 'Hooks/useEventHandler';
import { extractErrorMessage } from 'Helpers/utils';

export const extractSummaryFromAiResponse = (aiSummary: string): string => {
  const summaryParts = aiSummary.split('## Summary')?.[1]?.split('\n') || [];
  // Summary can either be
  // ## Summary: Some summary
  // or
  // ## Summary
  // Some summary
  let commentBody = summaryParts?.[0]?.trim() || '';
  if (!commentBody) {
    commentBody = summaryParts?.[1]?.trim() || '';
  }
  // Backwards compatibility with old AI summaries that start with a colon
  if (commentBody.startsWith(': ')) {
    commentBody = commentBody.slice(2);
  }

  // Convert markdown to HTML and handle quotes
  commentBody = commentBody
    .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
    .replace(/[*_]([^*_]+)[*_]/g, '<i>$1</i>');

  if (commentBody) {
    commentBody += '<br /><br />';
  }
  commentBody += '<i>This comment was auto-generated by Panther AI.</i>';
  commentBody += `<br /><br /><a href="${window.location.href}">Panther AI Triage Alert</a>`;
  return commentBody;
};

// ─── Comment row (rendered as first item inside the Next Steps card) ──────────

interface AlertTriageCommentRowV3Props {
  alertId: string;
  aiSummary: string;
}

/**
 * Renders as the first row inside the Next Steps card (the `header` slot of
 * RecommendedActionsV3). Styled to match the other list rows.
 */
export const AlertTriageCommentRowV3: React.FC<AlertTriageCommentRowV3Props> = ({
  alertId,
  aiSummary,
}) => {
  const { pushSnackbar } = useSnackbar();
  const {
    palette: { tokens },
  } = useTheme();
  const borderColor = tokens.stroke.neutral[4];

  const [loading, setLoading] = React.useState(false);
  const [posted, setPosted] = React.useState(false);

  React.useEffect(() => {
    setPosted(false);
  }, [aiSummary]);

  const commentBody = React.useMemo(() => extractSummaryFromAiResponse(aiSummary), [aiSummary]);

  const [createComment] = useCreateComment({
    variables: { input: { body: commentBody, alertId, format: CommentFormat.Html } },
  });

  const handleComment = useEventHandler(async () => {
    try {
      setLoading(true);
      await createComment({
        refetchQueries: [
          {
            query: AlertActivityHistoryDocument,
            variables: {
              id: alertId,
              activityHistoryPageInfo: { pageSize: ALERT_ACTIVITY_PAGE_SIZE },
            },
          },
        ],
      });
      setPosted(true);
      pushSnackbar({ variant: 'success', title: 'Comment added to alert' });
    } catch (error) {
      pushSnackbar({
        variant: 'error',
        title: 'Something went wrong',
        description: extractErrorMessage(error as ApolloError),
      });
    } finally {
      setLoading(false);
    }
  });

  return (
    <RoleRestrictedAccess allowedPermissions={[Permission.AlertModify]}>
      <Flex
        alignItems="center"
        gap={2}
        p={5}
        backgroundColor={tokens.background.neutral.raised[6]}
        sx={{ borderBottom: `1px solid ${borderColor}` }}
      >
        <Box flex={1} minWidth={0}>
          <Text
            variant="body/md"
            fontWeight="semibold"
            color={tokens.content.text.neutral.base}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            Add triage report as alert comment
          </Text>
        </Box>
        <Tooltip
          content="Persists the AI triage summary as an HTML comment on this alert without closing it"
          placement="top"
        >
          <Box flexShrink={0}>
            <Button
              variant="outline"
              size="small"
              disabled={loading || posted}
              loading={loading}
              onClick={handleComment}
            >
              <Flex align="center" gap={1}>
                <Icon type="comment-outline" size="x-small" sx={{ fontSize: '10px' }} />
                {posted ? 'Comment Added' : 'Add Comment'}
              </Flex>
            </Button>
          </Box>
        </Tooltip>
      </Flex>
    </RoleRestrictedAccess>
  );
};

// ─── Close actions row (rendered below the Next Steps card) ──────────────────

interface AlertTriageCloseActionsV3Props {
  alertId: string;
  alertStatus: AlertStatus;
  aiSummary: string;
  onClose?: () => void;
}

/**
 * Renders below the Next Steps card with "Close + Mark Resolved", "Close + Mark Invalid"
 * buttons on the left and an "Assign to me on close" toggle on the right.
 */
export const AlertTriageCloseActionsV3: React.FC<AlertTriageCloseActionsV3Props> = ({
  alertId,
  alertStatus,
  aiSummary,
  onClose,
}) => {
  const { pushSnackbar } = useSnackbar();
  const { userInfo } = useAuth();

  const isAlertClosed = alertStatus === AlertStatus.Closed || alertStatus === AlertStatus.Resolved;

  const [loading, setLoading] = React.useState<AlertStatus | null>(null);
  const [assignToMe, setAssignToMe] = React.useState(true);

  const commentBody = React.useMemo(() => extractSummaryFromAiResponse(aiSummary), [aiSummary]);

  const [createComment] = useCreateComment({
    variables: { input: { body: commentBody, alertId, format: CommentFormat.Html } },
  });

  const [updateAlertsAssigneeById] = useUpdateAlertsAssigneeById({
    variables: { input: { ids: [alertId], assigneeId: userInfo?.id } },
  });

  const [updateAlertStatusById] = useUpdateAlertStatusById({
    refetchQueries: [
      {
        query: AlertActivityHistoryDocument,
        variables: {
          id: alertId,
          activityHistoryPageInfo: { pageSize: ALERT_ACTIVITY_PAGE_SIZE },
        },
      },
    ],
  });

  const handleCommentAndClose = useEventHandler(async (newStatus: AlertStatus) => {
    try {
      setLoading(newStatus);
      await createComment();
      if (assignToMe) {
        await updateAlertsAssigneeById();
      }
      await updateAlertStatusById({
        variables: { input: { status: newStatus, ids: [alertId] } },
      });
      pushSnackbar({ variant: 'success', title: 'Alert closed and comment added' });
      onClose?.();
    } catch (error) {
      pushSnackbar({
        variant: 'error',
        title: 'Something went wrong',
        description: extractErrorMessage(error as ApolloError),
      });
    } finally {
      setLoading(null);
    }
  });

  const isButtonDisabled = loading !== null;

  return (
    <RoleRestrictedAccess allowedPermissions={[Permission.AlertModify]}>
      <Flex align="center" justify="space-between" py={3}>
        <Flex gap={2}>
          <Tooltip
            content="Add AI summary as a comment and mark this alert as Resolved"
            placement="top"
          >
            <Box>
              <Button
                variant="outline"
                size="small"
                borderRadius="pill"
                disabled={isButtonDisabled || isAlertClosed}
                loading={loading === AlertStatus.Resolved}
                onClick={() => handleCommentAndClose(AlertStatus.Resolved)}
              >
                <Flex align="center" gap={1}>
                  <Icon type="check-outline" size="small" />
                  Close + Mark Resolved
                </Flex>
              </Button>
            </Box>
          </Tooltip>

          <Tooltip
            content="Add AI summary as a comment and mark this alert as Invalid"
            placement="top"
          >
            <Box>
              <Button
                variant="outline"
                size="small"
                borderRadius="pill"
                disabled={isButtonDisabled || isAlertClosed}
                loading={loading === AlertStatus.Closed}
                onClick={() => handleCommentAndClose(AlertStatus.Closed)}
              >
                <Flex align="center" gap={1}>
                  <Icon type="filter-exclude" size="small" />
                  Close + Mark Invalid
                </Flex>
              </Button>
            </Box>
          </Tooltip>
        </Flex>

        <Switch
          checked={assignToMe}
          label="Assign to me on close"
          onChange={e => setAssignToMe(e.target.checked)}
          aria-label="Assign alert to me upon closing"
          labelPosition="right"
        />
      </Flex>
    </RoleRestrictedAccess>
  );
};
